import { useState } from 'react';
import type { FlatPrice, ReservationSpanPaymentLinkFilter } from '@repo/domain';
import {
  BusinessValidationCode,
  BusinessValidationError,
  formatFlatPrice,
  usePaynowClauses,
  useReservationSpanPaymentLinkActions,
} from '@repo/domain-service';
import { i18n, tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { toast } from '@repo/toast';
import { CodeInputGroup } from '@repo/ui';

import Header from '@features/reservation/components/Header';
import PaymentMethodCard from '@features/reservation/components/payment/PaymentMethodCard';
import PaynowClauses from '@features/reservation/components/payment/PaynowClauses';
import { BLIK_LENGTH, PaymentMethod } from '@features/reservation/constants';
import { PAYMENT_METHODS } from '@features/reservation/constants/paymentMethods';
import { type PaymentMethodId } from '@features/reservation/types';
import { rememberSpanPaymentLinkRedirect } from '@features/reservation/utils/spanPaymentLinkRedirect';

import PageFrame from '@shared/components/page/PageFrame';
import { handleRepositoryError } from '@shared/utils/handleRepositoryError';
import { useLanguageStore } from '@shared/stores/useLanguageStore';

type LinkPaymentMethodId = Exclude<PaymentMethodId, 'wallet'>;

// Nobody is signed in as the renter, so the wallet is never offered.
const LINK_PAYMENT_METHODS = PAYMENT_METHODS.filter((method) => method.id !== PaymentMethod.Wallet);

interface Props {
  testId: TestId;
  filter: ReservationSpanPaymentLinkFilter;
  price: FlatPrice | undefined;
  onGoBack: () => void;
  /** A BLIK payment was accepted and awaits the confirmation in the bank app. */
  onBlikPaymentInitiated: () => void;
  /** The backend refused the link while paying. */
  onLinkInvalid: () => void;
}

export default function SpanPaymentLinkMethodsContents({
  testId,
  filter,
  price,
  onGoBack,
  onBlikPaymentInitiated,
  onLinkInvalid,
}: Props) {
  const language = useLanguageStore((state) => state.language);
  const { paynowClauses, loadingPaynowClauses } = usePaynowClauses({ language });
  const blikClausesReady = paynowClauses.length > 0;
  const { payReservationSpanPaymentLink, payingReservationSpanPaymentLink } = useReservationSpanPaymentLinkActions();

  const [pickedMethodId, setPickedMethodId] = useState<LinkPaymentMethodId | undefined>(undefined);
  const defaultMethodId: LinkPaymentMethodId | undefined = loadingPaynowClauses
    ? undefined
    : blikClausesReady
      ? PaymentMethod.Blik
      : PaymentMethod.Paynow;
  const selectedMethodId = pickedMethodId ?? defaultMethodId;
  const [blikCode, setBlikCode] = useState<string>('');
  const [paymentDispatched, setPaymentDispatched] = useState<boolean>(false);
  const isConfirmPossible: boolean =
    (selectedMethodId === PaymentMethod.Blik && !!blikCode) || selectedMethodId === PaymentMethod.Paynow;

  const formattedPrice = price ? ` • ${formatFlatPrice(price, i18n.language)}` : '';

  const submitPayment = async (enteredBlikCode: string) => {
    if (!selectedMethodId) return;

    setPaymentDispatched(true);
    try {
      const response = await payReservationSpanPaymentLink({
        ...filter,
        paymentMethod: selectedMethodId,
        blikCode: selectedMethodId === PaymentMethod.Blik ? enteredBlikCode : undefined,
      });

      if (response.paymentUrl) {
        rememberSpanPaymentLinkRedirect();
        window.location.assign(response.paymentUrl);
        return;
      }
      onBlikPaymentInitiated();
    } catch (error) {
      setPaymentDispatched(false);
      if (
        error instanceof BusinessValidationError &&
        error.code === BusinessValidationCode.reservationSpanPaymentLinkInvalid
      ) {
        onLinkInvalid();
        return;
      }
      handleRepositoryError(error, {
        fallback: tr('reservations:span_payment_link.payment_error'),
        testId,
        businessCodes: {
          [BusinessValidationCode.reservationFailedDueToPendingExtensionExists]: tr(
            'reservations:span_payment_link.payment_pending'
          ),
        },
      });
    }
  };

  const handleBlikComplete = (code: string) => {
    setBlikCode(code);
    if (payingReservationSpanPaymentLink || paymentDispatched) return;
    void submitPayment(code);
  };

  const handlePasteError = () => {
    toast.danger(tr('reservations:blik.clipboard_access_denied'), testId.append('clipboardError'));
  };

  const handlePaymentMethodSelection = (methodId: LinkPaymentMethodId): void => {
    setPickedMethodId(methodId);
    if (methodId !== PaymentMethod.Blik) setBlikCode('');
  };

  const handleConfirmPayment = async () => {
    if (!isConfirmPossible || paymentDispatched) return;
    await submitPayment(blikCode);
  };

  const handleBack = () => {
    onGoBack();
  };

  return (
    <PageFrame
      testId={testId}
      onGoBack={handleBack}
      forwardButton={{
        label: tr('reservations:payment_page.confirm_and_pay') + formattedPrice,
        disabled: !isConfirmPossible || paymentDispatched,
        pending: payingReservationSpanPaymentLink || paymentDispatched,
        onClick: handleConfirmPayment,
      }}
    >
      <Header
        title={tr('reservations:payment_page.title')}
        subtitle={tr('reservations:payment_page.subtitle')}
        testId={testId.append('title')}
      />

      <div className="bg-primary flex flex-col gap-3 rounded-3xl p-4">
        {LINK_PAYMENT_METHODS.map((method) => {
          const methodId = method.id as LinkPaymentMethodId;
          const handleMethodSelection = () => handlePaymentMethodSelection(methodId);
          return (
            <PaymentMethodCard
              key={method.id}
              selectedMethod={selectedMethodId === method.id}
              onMethodSelection={handleMethodSelection}
              icon={method.icon}
              title={tr(method.titleTranslationKey)}
              subtitle={method.subtitleTranslationKey ? `${tr(method.subtitleTranslationKey)} *` : undefined}
              testId={testId.append(`${method.id}Method`)}
              disabled={Boolean(method.disabled) || (method.id === PaymentMethod.Blik && !blikClausesReady)}
            >
              {method.hasExpandableContent && method.id === PaymentMethod.Blik && (
                <>
                  <CodeInputGroup
                    // Remount on (de)selection so `autoFocus` re-runs and lands the cursor on the first digit.
                    key={selectedMethodId === PaymentMethod.Blik ? 'blikInputActive' : 'blikInputInactive'}
                    testId={testId.append('blikInput')}
                    onComplete={handleBlikComplete}
                    variant="connected"
                    disabled={selectedMethodId !== PaymentMethod.Blik}
                    autoFocus={selectedMethodId === PaymentMethod.Blik}
                    length={BLIK_LENGTH}
                    label={method.labelTranslationKey ? tr(method.labelTranslationKey) : undefined}
                    required
                    onPasteError={handlePasteError}
                  />
                  {selectedMethodId === PaymentMethod.Blik && (
                    <PaynowClauses testId={testId.append('paynowClauses')} language={language} />
                  )}
                </>
              )}
            </PaymentMethodCard>
          );
        })}
      </div>
    </PageFrame>
  );
}
